import { useCallback, useEffect, useRef, useState } from 'react';

export type BrowserSpeechLang = 'en-US' | 'es-ES';

function getSpeechRecognitionCtor(): (new () => SpeechRecognition) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognition;
    webkitSpeechRecognition?: new () => SpeechRecognition;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

/**
 * Browser Web Speech API (Chrome/Edge/Safari desktop). Requires HTTPS (or localhost) and mic permission.
 */
export function useSpeechRecognition(options: {
  lang: BrowserSpeechLang;
  onFinal: (transcript: string) => void;
}) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const optsRef = useRef(options);
  optsRef.current = options;

  useEffect(() => {
    setSupported(!!getSpeechRecognitionCtor());
  }, []);

  const stop = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;
    setListening(false);
  }, []);

  const start = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('Speech recognition not supported in this browser.');
      return;
    }
    setError(null);
    try {
      const rec = new Ctor();
      rec.lang = optsRef.current.lang;
      rec.continuous = false;
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.onresult = (ev: SpeechRecognitionEvent) => {
        const text = ev.results[0]?.[0]?.transcript?.trim();
        if (text) optsRef.current.onFinal(text);
      };
      rec.onerror = (ev: SpeechRecognitionErrorEvent) => {
        if (ev.error === 'aborted') return;
        if (ev.error === 'no-speech' || ev.error === 'audio-capture') {
          setError(ev.error === 'no-speech' ? null : ev.message);
          return;
        }
        setError(ev.message || ev.error);
      };
      rec.onend = () => {
        setListening(false);
        recognitionRef.current = null;
      };
      recognitionRef.current = rec;
      setListening(true);
      rec.start();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start microphone');
      setListening(false);
    }
  }, []);

  useEffect(
    () => () => {
      try {
        recognitionRef.current?.abort();
      } catch {
        /* ignore */
      }
    },
    []
  );

  return { supported, listening, error, start, stop, clearError: () => setError(null) };
}
